import React, { useCallback, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import TextField from "./TextField";
import LookUpField from "./LookUpField";
import SelectField from "./SelectField";
import { OfferWindowFormValues, OfferWindowRow } from "../../../../../models/vod/Models";

interface Props {
    ingestorProperties: Record<string, OfferWindowRow>;
    newOfferWindowInitialValues? : Partial<OfferWindowRow>;
}

const OfferWindowUpsertForm = ({
    ingestorProperties,
    newOfferWindowInitialValues = { },
}: Props) => {
    const { control } = useFormContext<OfferWindowFormValues>();
    const { fields, append, remove } = useFieldArray({
        control,
        name: "offerWindows",
    });

    const getTenantName = useCallback(
        (offerWindow: OfferWindowRow) => {
            return Object.values(ingestorProperties)
                .find((ingestorOfferWindow) => {
                    return ingestorOfferWindow.depCode === offerWindow.depCode
                        && ingestorOfferWindow.tenantId === offerWindow.tenantId
                        && ingestorOfferWindow.offerType === offerWindow.offerType
                        && JSON.stringify(ingestorOfferWindow.catalogues?.map(c => c.id))
                        === JSON.stringify(offerWindow.catalogues?.map(c => c.id));
                })?.tenantName;
        },
        [ ingestorProperties ]
    );

    const getIdentifier = useCallback(
        (offerWindow: OfferWindowRow) =>
        `${getTenantName(offerWindow) ?? offerWindow.tenantId} - ${offerWindow.offerType} ${offerWindow.displayDescription ? `- ${offerWindow.displayDescription}` :  ""}`,
        [ getTenantName ]
    );

    const options = useMemo(() => {
        return Object.entries(ingestorProperties)
                .map(([ key, offerWindow ]) =>
                    [ key, getIdentifier(offerWindow) ])
                .sort((a, b) => a[1] > b[1] ? 1 : -1);
    }, [ ingestorProperties, getIdentifier ]);

    const optionsExists = new Set(
        fields
            .map(liveField => getIdentifier(liveField).toLowerCase())
    );

    const selectFieldOptions: Record<string, string> = Object.fromEntries(options.filter((option) => !optionsExists.has(option[1].toLowerCase())));

    return (
        <>
            <div className={`gap-2 flex flex-col`}>
                {fields.map((field, i) => {
                    const itemBasePath = `offerWindows.${i}`;
                    const isNew = field.isNew;

                    return (
                        <div
                            key={`${field.id}`}
                            className={`px-2 pt-2 relative`}
                        >
                            <div
                                className={`p-2 border-brand-500/10 dark:border-white/10 rounded-md border`}
                            >
                                <label className={`dark:text-gray-100 mb-1`}>
                                    {"Offer Window"}
                                </label>
                                <div className={`px-2 pt-2 relative`}>
                                    {
                                        <SelectField
                                            label={"Offer"}
                                            name={`${itemBasePath}.ingestorId`}
                                            options={selectFieldOptions}
                                            isReadOnly ={!isNew}
                                            initialOption={`${getTenantName(field) ?? field.tenantId} - ${field.offerType} ${field.displayDescription ? `- ${field.displayDescription}` :  ""}`}
                                        />
                                    }
                                    <LookUpField
                                        targetName={`${itemBasePath}.offerType`}
                                        label={"Type"}
                                        sourceName={`${itemBasePath}.ingestorId`}
                                        lookup={ingestorProperties}
                                        keyField={`offerType`}
                                        initialValue={String(
                                            field.offerType ?? "",
                                        )}
                                    />
                                    <LookUpField
                                        targetName={`${itemBasePath}.tenantId`}
                                        label={"Tenant"}
                                        sourceName={`${itemBasePath}.ingestorId`}
                                        lookup={ingestorProperties}
                                        keyField={`tenantId`}
                                        initialValue={String(
                                            field.tenantId ?? "",
                                        )}
                                    />
                                    <LookUpField
                                        targetName={`${itemBasePath}.depCode`}
                                        label={"Dep Code"}
                                        sourceName={`${itemBasePath}.ingestorId`}
                                        lookup={ingestorProperties}
                                        keyField={`depCode`}
                                        initialValue={String(
                                            field.depCode ?? "",
                                        )}
                                    />
                                    <LookUpField
                                        targetName={`${itemBasePath}.displayDescription`}
                                        label={"Description"}
                                        sourceName={`${itemBasePath}.ingestorId`}
                                        lookup={ingestorProperties}
                                        keyField={`displayDescription`}
                                        initialValue={String(
                                            field.displayDescription ?? "",
                                        )}
                                    />
                                    <TextField
                                        name={`${itemBasePath}.startDateTime`}
                                        type={"datetime-local"}
                                        label={"Start Date"}
                                    />
                                    <TextField
                                        name={`${itemBasePath}.endDateTime`}
                                        type={"datetime-local"}
                                        label={"End Date"}
                                    />
                                    {
                                        (isNew || field.tenantId !== "DEFAULT")
                                        && <button
                                            type="button"
                                            className={`top-1 right-2 border-brand-500/10 dark:border-brand-300/10 rounded-lg p-1 absolute border`}
                                            onClick={() => remove(i)}
                                           >
                                            remove
                                           </button>
                                    }

                                </div>
                            </div>
                        </div>
                    );
                })}
                <div className={`flex justify-end`}>
                    <button
                        type="button"
                        className={`text-white bg-blue-800 dark:bg-brand-300 rounded-md shadow-xl p-2 w-fit`}
                        onClick={() => append({
                            ...newOfferWindowInitialValues,
                            isNew: true,
                        })}
                    >
                        {`Add more Offer Windows`}
                    </button>
                </div>
            </div>
        </>
    );
};

export default OfferWindowUpsertForm;